import { useState, useEffect, useMemo } from 'react'
import { Link } from 'react-router-dom'
import {
  ChevronLeftIcon,
  ChevronRightIcon,
  CalendarIcon,
  VideoCameraIcon,
  MapPinIcon,
} from '@heroicons/react/24/outline'
import { Badge } from '@/components/ui'
import { eventsApi } from '@/api'
import type { CalendarEvent } from '@/api/events'
import {
  format,
  startOfMonth,
  endOfMonth,
  startOfWeek,
  endOfWeek,
  addDays,
  addMonths,
  subMonths,
  isSameMonth,
  isSameDay,
  isToday,
  parseISO,
} from 'date-fns'
import { it } from 'date-fns/locale'

export default function EventCalendar() {
  const [currentMonth, setCurrentMonth] = useState(new Date())
  const [events, setEvents] = useState<CalendarEvent[]>([])
  const [_loading, setLoading] = useState(true)
  const [selectedDate, setSelectedDate] = useState<Date | null>(null)

  // Fetch events for the current month view
  useEffect(() => {
    const fetchEvents = async () => {
      setLoading(true)
      try {
        const monthStart = startOfMonth(currentMonth)
        const monthEnd = endOfMonth(currentMonth)
        // Extend range to include partial weeks
        const viewStart = startOfWeek(monthStart, { weekStartsOn: 1 })
        const viewEnd = endOfWeek(monthEnd, { weekStartsOn: 1 })

        const data = await eventsApi.getCalendarEvents(
          format(viewStart, 'yyyy-MM-dd'),
          format(viewEnd, 'yyyy-MM-dd')
        )
        setEvents(data)
      } catch (error) {
        console.error('Error fetching calendar events:', error)
        setEvents([])
      } finally {
        setLoading(false)
      }
    }

    fetchEvents()
  }, [currentMonth])

  // Group events by date
  const eventsByDate = useMemo(() => {
    const grouped: Record<string, CalendarEvent[]> = {}
    events.forEach((event) => {
      const dateKey = format(parseISO(event.start_datetime), 'yyyy-MM-dd')
      if (!grouped[dateKey]) {
        grouped[dateKey] = []
      }
      grouped[dateKey].push(event)
    })
    return grouped
  }, [events])

  // Generate calendar days
  const calendarDays = useMemo(() => {
    const monthStart = startOfMonth(currentMonth)
    const monthEnd = endOfMonth(currentMonth)
    const startDate = startOfWeek(monthStart, { weekStartsOn: 1 })
    const endDate = endOfWeek(monthEnd, { weekStartsOn: 1 })

    const days: Date[] = []
    let day = startDate
    while (day <= endDate) {
      days.push(day)
      day = addDays(day, 1)
    }
    return days
  }, [currentMonth])

  const navigateMonth = (direction: 'prev' | 'next') => {
    setCurrentMonth(direction === 'prev' ? subMonths(currentMonth, 1) : addMonths(currentMonth, 1))
    setSelectedDate(null)
  }

  const goToToday = () => {
    setCurrentMonth(new Date())
    setSelectedDate(new Date())
  }

  const getEventTypeColor = (type: string) => {
    const colors: Record<string, string> = {
      webinar: 'bg-blue-500',
      workshop: 'bg-green-500',
      conference: 'bg-purple-500',
      community_day: 'bg-orange-500',
      training: 'bg-cyan-500',
      meeting: 'bg-gray-500',
      other: 'bg-gray-400',
    }
    return colors[type] || 'bg-primary-500'
  }

  const selectedDateEvents = selectedDate
    ? eventsByDate[format(selectedDate, 'yyyy-MM-dd')] || []
    : []

  return (
    <div className="bg-white rounded-xl shadow-sm border border-gray-200 overflow-hidden">
      {/* Header */}
      <div className="px-6 py-4 border-b border-gray-200 flex items-center justify-between">
        <div className="flex items-center space-x-4">
          <button
            onClick={() => navigateMonth('prev')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronLeftIcon className="h-5 w-5 text-gray-600" />
          </button>
          <h2 className="text-lg font-semibold text-gray-900 min-w-[180px] text-center capitalize">
            {format(currentMonth, 'MMMM yyyy', { locale: it })}
          </h2>
          <button
            onClick={() => navigateMonth('next')}
            className="p-2 hover:bg-gray-100 rounded-lg transition-colors"
          >
            <ChevronRightIcon className="h-5 w-5 text-gray-600" />
          </button>
        </div>
        <button
          onClick={goToToday}
          className="px-4 py-2 text-sm font-medium text-primary-600 hover:bg-primary-50 rounded-lg transition-colors"
        >
          Oggi
        </button>
      </div>

      <div className="flex">
        {/* Calendar Grid */}
        <div className="flex-1 p-4">
          {/* Weekday Headers */}
          <div className="grid grid-cols-7 mb-2">
            {['Lun', 'Mar', 'Mer', 'Gio', 'Ven', 'Sab', 'Dom'].map((day) => (
              <div key={day} className="text-center text-xs font-medium text-gray-500 py-2">
                {day}
              </div>
            ))}
          </div>

          {/* Calendar Days */}
          <div className="grid grid-cols-7 gap-1">
            {calendarDays.map((day, idx) => {
              const dateKey = format(day, 'yyyy-MM-dd')
              const dayEvents = eventsByDate[dateKey] || []
              const isCurrentMonth = isSameMonth(day, currentMonth)
              const isSelected = selectedDate && isSameDay(day, selectedDate)
              const isTodayDate = isToday(day)

              return (
                <button
                  key={idx}
                  onClick={() => setSelectedDate(day)}
                  className={`
                    relative min-h-[80px] p-1 border rounded-lg text-left transition-all
                    ${isCurrentMonth ? 'bg-white' : 'bg-gray-50'}
                    ${isSelected ? 'border-primary-500 ring-2 ring-primary-200' : 'border-gray-100 hover:border-gray-300'}
                  `}
                >
                  <span
                    className={`
                      inline-flex items-center justify-center w-7 h-7 text-sm rounded-full
                      ${isTodayDate ? 'bg-primary-600 text-white font-bold' : ''}
                      ${!isTodayDate && isCurrentMonth ? 'text-gray-900' : 'text-gray-400'}
                    `}
                  >
                    {format(day, 'd')}
                  </span>

                  {/* Event dots/pills */}
                  <div className="mt-1 space-y-0.5">
                    {dayEvents.slice(0, 3).map((event) => (
                      <div
                        key={event.id}
                        className={`
                          text-[10px] px-1.5 py-0.5 rounded truncate text-white
                          ${getEventTypeColor(event.type)}
                        `}
                        title={event.title}
                      >
                        {event.title}
                      </div>
                    ))}
                    {dayEvents.length > 3 && (
                      <div className="text-[10px] text-gray-500 px-1">
                        +{dayEvents.length - 3} altri
                      </div>
                    )}
                  </div>
                </button>
              )
            })}
          </div>

          {/* Legend */}
          <div className="mt-4 flex flex-wrap gap-3 text-xs">
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-blue-500"></span>
              <span>Webinar</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-green-500"></span>
              <span>Workshop</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-purple-500"></span>
              <span>Conferenza</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-orange-500"></span>
              <span>Community Day</span>
            </div>
            <div className="flex items-center gap-1">
              <span className="w-3 h-3 rounded bg-cyan-500"></span>
              <span>Formazione</span>
            </div>
          </div>
        </div>

        {/* Selected Date Events Panel */}
        <div className="w-80 border-l border-gray-200 bg-gray-50 p-4">
          <h3 className="font-medium text-gray-900 mb-4">
            {selectedDate ? (
              <span className="capitalize">
                {format(selectedDate, "EEEE d MMMM", { locale: it })}
              </span>
            ) : (
              'Seleziona un giorno'
            )}
          </h3>

          {selectedDate && (
            <div className="space-y-3">
              {selectedDateEvents.length > 0 ? (
                selectedDateEvents.map((event) => (
                  <Link
                    key={event.id}
                    to={`/events/${event.slug}`}
                    className="block p-3 bg-white rounded-lg border border-gray-200 hover:shadow-md transition-shadow"
                  >
                    <div className="flex items-start gap-2">
                      <div className={`w-1 h-full min-h-[40px] rounded ${getEventTypeColor(event.type)}`} />
                      <div className="flex-1 min-w-0">
                        <p className="font-medium text-gray-900 text-sm truncate">
                          {event.title}
                        </p>
                        <div className="flex items-center gap-2 mt-1 text-xs text-gray-500">
                          <span>
                            {format(parseISO(event.start_datetime), 'HH:mm')} -{' '}
                            {format(parseISO(event.end_datetime), 'HH:mm')}
                          </span>
                          {event.is_online ? (
                            <Badge size="sm" variant="info">
                              <VideoCameraIcon className="h-3 w-3 mr-1" />
                              Online
                            </Badge>
                          ) : (
                            <Badge size="sm" variant="default">
                              <MapPinIcon className="h-3 w-3 mr-1" />
                              In presenza
                            </Badge>
                          )}
                        </div>
                      </div>
                    </div>
                  </Link>
                ))
              ) : (
                <div className="text-center py-8 text-gray-500">
                  <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
                  <p className="text-sm">Nessun evento in questo giorno</p>
                </div>
              )}
            </div>
          )}

          {!selectedDate && (
            <div className="text-center py-8 text-gray-500">
              <CalendarIcon className="h-12 w-12 mx-auto mb-2 text-gray-300" />
              <p className="text-sm">Clicca su un giorno per vedere gli eventi</p>
            </div>
          )}
        </div>
      </div>
    </div>
  )
}
